// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Configuracoes/ConfiguracaoNfse.tsx
//  Configuração da emissão NFS-e — integração com ADN/SEFIN Nacional.
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import {
  ShieldCheck, FileCheck, KeyRound, Globe, Save, AlertTriangle,
  CheckCircle2, Server, FolderOpen, Hash, Building, UserCog, Image as ImageIcon, X,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useConfigNfse } from '@/features/configuracoes/hooks/useConfigNfse';
import type { SalvarConfigNfseRequest } from '@/features/configuracoes/services/configNfseService';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

// ──────────────────────────────────────────────────────────────────────────────
//  Componente: corpo reutilizável (sem header de página)
// ──────────────────────────────────────────────────────────────────────────────

export function NfseConfigForm({ empresaId, showSaveButton = true }: { empresaId: number; showSaveButton?: boolean }) {
  const { config, isLoading, salvar, isSaving, validarCert, isValidating, validacaoData } =
    useConfigNfse(empresaId);

  const [form, setForm] = useState<SalvarConfigNfseRequest>(initialForm());

  useEffect(() => {
    if (config) {
      setForm({
        emiteNfse:             config.emiteNfse,
        numeroRps:             config.numeroRps,
        numeroNfse:            config.numeroNfse,
        usaServidorNacional:   config.usaServidorNacional,
        serieRps:              config.serieRps,
        serieNfse:             config.serieNfse,
        tipoAmbiente:          config.tipoAmbiente,
        diretorioXml:          config.diretorioXml,
        diretorioPdf:          config.diretorioPdf,
        cidadeHomologada:      config.cidadeHomologada,
        incentivadorCultural:  config.incentivadorCultural,
        certificadoCaminho:    config.certificadoCaminho,
        certificadoSenha:      undefined,
        certificadoThumbprint: config.certificadoThumbprint,
        urlAdnBase:            config.urlAdnBase,
        urlSefinBase:          config.urlSefinBase,
        usuarioWebService:     config.usuarioWebService,
        senhaWebService:       undefined,
        fraseSecreta:          undefined,
        chaveAcesso:           undefined,
        chaveAutorizacao:      undefined,
        codigoSiafi:           config.codigoSiafi,
        aguardarRetorno:       config.aguardarRetorno,
        tentativas:            config.tentativas,
        intervaloTentativas:   config.intervaloTentativas,
        ibAjusteTempos:        config.ibAjusteTempos,
        diretorioArquivoCidades: config.diretorioArquivoCidades,
        cnpjPrefeitura:        config.cnpjPrefeitura,
        nomePrefeitura:        config.nomePrefeitura,
        logoPrefeituraBase64:  undefined,
      });
    }
  }, [config]);

  const upd = <K extends keyof SalvarConfigNfseRequest>(k: K, v: SalvarConfigNfseRequest[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const submit = () => salvar(form);

  if (empresaId <= 0) return <div className="text-muted-foreground">Sessão sem empresa.</div>;
  if (isLoading)      return <div className="space-y-3"><Skeleton className="h-10 w-72" /><Skeleton className="h-72" /></div>;

  return (
    <div className="space-y-6">
      {/* Banner status */}
      {!config && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>NFS-e ainda não configurada</AlertTitle>
          <AlertDescription>
            Preencha os campos abaixo, salve, e em seguida valide o certificado A1/A3 ICP-Brasil.
          </AlertDescription>
        </Alert>
      )}

      {config?.certificadoConfigurado && config.certificadoValidade && (
        <Alert className={
          new Date(config.certificadoValidade) < new Date()
            ? 'border-rose-500/50 bg-rose-500/5'
            : (new Date(config.certificadoValidade).getTime() - Date.now()) / 86_400_000 < 30
              ? 'border-amber-500/50 bg-amber-500/5'
              : 'border-emerald-500/50 bg-emerald-500/5'
        }>
          <CheckCircle2 className="h-4 w-4" />
          <AlertTitle>Certificado configurado</AlertTitle>
          <AlertDescription>
            Validade: {new Date(config.certificadoValidade).toLocaleDateString('pt-BR')} ·{' '}
            {Math.max(0, Math.ceil((new Date(config.certificadoValidade).getTime() - Date.now()) / 86_400_000))} dia(s) restante(s).
          </AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="emissao">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="emissao">Emissão</TabsTrigger>
          <TabsTrigger value="ambiente">Ambiente / URLs</TabsTrigger>
          <TabsTrigger value="certificado">Certificado ICP-Brasil</TabsTrigger>
          <TabsTrigger value="provedor">Provedor / Tempos</TabsTrigger>
          <TabsTrigger value="prefeitura">Prefeitura</TabsTrigger>
          <TabsTrigger value="diretorios">Diretórios</TabsTrigger>
        </TabsList>

        <TabsContent value="emissao">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileCheck className="h-4 w-4" /> Parâmetros de emissão
              </CardTitle>
              <CardDescription>
                Numeração de RPS, série, e flags de comportamento.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3 flex items-center gap-3">
                <Switch
                  id="emite"
                  checked={form.emiteNfse}
                  onCheckedChange={(v) => upd('emiteNfse', v)}
                />
                <Label htmlFor="emite">Empresa emite NFS-e</Label>
              </div>

              <div>
                <Label>Próximo número de RPS</Label>
                <Input
                  type="number"
                  value={form.numeroRps ?? ''}
                  onChange={(e) => upd('numeroRps', e.target.value ? Number(e.target.value) : undefined)}
                />
              </div>
              <div>
                <Label>Série RPS</Label>
                <Input
                  maxLength={5}
                  value={form.serieRps ?? ''}
                  onChange={(e) => upd('serieRps', e.target.value || undefined)}
                  placeholder="Ex.: 1"
                />
              </div>
              <div>
                <Label>Próximo número de NFS-e</Label>
                <Input
                  type="number"
                  value={form.numeroNfse ?? ''}
                  onChange={(e) => upd('numeroNfse', e.target.value ? Number(e.target.value) : undefined)}
                />
              </div>
              <div>
                <Label>Série NFS-e</Label>
                <Input
                  maxLength={5}
                  value={form.serieNfse ?? ''}
                  onChange={(e) => upd('serieNfse', e.target.value || undefined)}
                  placeholder="Ex.: 1"
                />
              </div>

              <div className="flex items-center gap-3">
                <Switch
                  id="incent"
                  checked={form.incentivadorCultural}
                  onCheckedChange={(v) => upd('incentivadorCultural', v)}
                />
                <Label htmlFor="incent">Incentivador cultural</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  id="serv-nac"
                  checked={form.usaServidorNacional}
                  onCheckedChange={(v) => upd('usaServidorNacional', v)}
                />
                <Label htmlFor="serv-nac">Usar servidor nacional (ADN)</Label>
              </div>
              <div>
                <Label>Cidade homologada (IBGE)</Label>
                <Input
                  type="number"
                  value={form.cidadeHomologada ?? ''}
                  onChange={(e) => upd('cidadeHomologada', e.target.value ? Number(e.target.value) : undefined)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ambiente">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Globe className="h-4 w-4" /> Ambiente ADN / SEFIN Nacional
              </CardTitle>
              <CardDescription>
                URLs default são preenchidas automaticamente conforme o ambiente
                escolhido. Sobrescreva apenas se a Receita Federal alterar os endpoints.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Ambiente</Label>
                <Select
                  value={String(form.tipoAmbiente ?? 2)}
                  onValueChange={(v) => upd('tipoAmbiente', Number(v))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2">2 — Homologação (Produção restrita)</SelectItem>
                    <SelectItem value="1">1 — Produção</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Default homologação:{' '}
                  <span className="font-mono">https://adn.producaorestrita.nfse.gov.br</span>
                </p>
              </div>

              <div>
                <Label>URL ADN (opcional, sobrescreve default)</Label>
                <Input
                  value={form.urlAdnBase ?? ''}
                  onChange={(e) => upd('urlAdnBase', e.target.value || undefined)}
                  placeholder="https://adn.nfse.gov.br"
                />
              </div>
              <div className="md:col-span-2">
                <Label>URL SEFIN Nacional (opcional)</Label>
                <Input
                  value={form.urlSefinBase ?? ''}
                  onChange={(e) => upd('urlSefinBase', e.target.value || undefined)}
                  placeholder="https://sefin.nfse.gov.br"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="certificado">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <KeyRound className="h-4 w-4" /> Certificado A1/A3 ICP-Brasil
              </CardTitle>
              <CardDescription>
                Use <strong>Caminho do .pfx + Senha</strong> para certificados A1, ou{' '}
                <strong>Thumbprint</strong> para A3 instalados no Windows Store.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label className="flex items-center gap-2">
                    <FolderOpen className="h-3 w-3" /> Caminho do arquivo .pfx (A1)
                  </Label>
                  <Input
                    value={form.certificadoCaminho ?? ''}
                    onChange={(e) => upd('certificadoCaminho', e.target.value || undefined)}
                    placeholder="C:\certificados\udisys.pfx"
                  />
                </div>
                <div>
                  <Label>Senha do certificado</Label>
                  <Input
                    type="password"
                    value={form.certificadoSenha ?? ''}
                    onChange={(e) => upd('certificadoSenha', e.target.value || undefined)}
                    placeholder={config?.certificadoConfigurado ? '•••••• (não enviar para manter)' : ''}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Deixe em branco para preservar a senha já salva.
                  </p>
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <Hash className="h-3 w-3" /> OU Thumbprint (A3)
                  </Label>
                  <Input
                    value={form.certificadoThumbprint ?? ''}
                    onChange={(e) => upd('certificadoThumbprint', e.target.value || undefined)}
                    placeholder="40 caracteres hex"
                    className="font-mono text-xs"
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-3 items-center pt-2">
                <Button
                  variant="outline"
                  onClick={() => validarCert()}
                  disabled={isValidating || !config?.certificadoConfigurado}
                  className="gap-2"
                >
                  <ShieldCheck className="h-4 w-4" />
                  {isValidating ? 'Validando…' : 'Validar certificado'}
                </Button>
                {!config?.certificadoConfigurado && (
                  <span className="text-xs text-muted-foreground">
                    Salve a configuração primeiro para validar.
                  </span>
                )}
              </div>

              {validacaoData && (
                <Alert className={validacaoData.sucesso ? 'border-emerald-500/50' : 'border-rose-500/50'}>
                  <Server className="h-4 w-4" />
                  <AlertTitle>
                    {validacaoData.sucesso ? 'Certificado válido' : 'Falha na validação'}
                  </AlertTitle>
                  <AlertDescription>
                    {validacaoData.sucesso ? (
                      <div className="space-y-1 mt-2 text-xs">
                        <div><strong>Subject:</strong> {validacaoData.subject}</div>
                        <div><strong>Emissor:</strong> {validacaoData.emissor}</div>
                        <div>
                          <strong>Vigência:</strong>{' '}
                          {validacaoData.validoDe && new Date(validacaoData.validoDe).toLocaleDateString('pt-BR')}
                          {' → '}
                          {validacaoData.validoAte && new Date(validacaoData.validoAte).toLocaleDateString('pt-BR')}
                          {validacaoData.diasParaExpirar !== undefined && (
                            <Badge
                              variant="outline"
                              className={`ml-2 ${
                                validacaoData.diasParaExpirar < 30
                                  ? 'border-amber-500/50 text-amber-600'
                                  : 'border-emerald-500/50 text-emerald-600'
                              }`}
                            >
                              {validacaoData.diasParaExpirar}d
                            </Badge>
                          )}
                        </div>
                      </div>
                    ) : (
                      <span className="text-sm">{validacaoData.mensagem}</span>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="provedor">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <UserCog className="h-4 w-4" /> Credenciais do provedor
              </CardTitle>
              <CardDescription>
                Algumas prefeituras municipais exigem usuário/senha próprios além do certificado ICP-Brasil.
                Preencha apenas o que sua prefeitura cadastrou.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Usuário WebService</Label>
                  <Input
                    value={form.usuarioWebService ?? ''}
                    onChange={(e) => upd('usuarioWebService', e.target.value || undefined)}
                  />
                </div>
                <div>
                  <Label>Senha WebService</Label>
                  <Input
                    type="password"
                    value={form.senhaWebService ?? ''}
                    onChange={(e) => upd('senhaWebService', e.target.value || undefined)}
                    placeholder={config?.usuarioWebService ? '•••••• (não enviar para manter)' : ''}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Deixe em branco para preservar a senha já salva.
                  </p>
                </div>
                <div>
                  <Label>Frase secreta</Label>
                  <Input
                    type="password"
                    value={form.fraseSecreta ?? ''}
                    onChange={(e) => upd('fraseSecreta', e.target.value || undefined)}
                    placeholder={config?.fraseSecretaConfigurada ? '•••••• (não enviar para manter)' : ''}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Deixe em branco para preservar o valor já salvo.
                  </p>
                </div>
                <div>
                  <Label>Código SIAFI da cidade</Label>
                  <Input
                    value={form.codigoSiafi ?? ''}
                    onChange={(e) => upd('codigoSiafi', e.target.value || undefined)}
                    placeholder="Ex.: 7107"
                  />
                </div>
                <div>
                  <Label>Chave de acesso</Label>
                  <Input
                    type="password"
                    value={form.chaveAcesso ?? ''}
                    onChange={(e) => upd('chaveAcesso', e.target.value || undefined)}
                    placeholder={config?.chaveAcessoConfigurada ? '•••••• (não enviar para manter)' : ''}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Deixe em branco para preservar o valor já salvo.
                  </p>
                </div>
                <div>
                  <Label>Chave de autorização</Label>
                  <Input
                    type="password"
                    value={form.chaveAutorizacao ?? ''}
                    onChange={(e) => upd('chaveAutorizacao', e.target.value || undefined)}
                    placeholder={config?.chaveAutorizacaoConfigurada ? '•••••• (não enviar para manter)' : ''}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Deixe em branco para preservar o valor já salvo.
                  </p>
                </div>
              </div>

              <div className="border-t pt-4 mt-2 space-y-4">
                <h4 className="text-sm font-medium">Tempos e retentativa</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Aguardar retorno (ms)</Label>
                    <Input
                      type="number"
                      value={form.aguardarRetorno ?? ''}
                      onChange={(e) => upd('aguardarRetorno', e.target.value ? Number(e.target.value) : undefined)}
                      placeholder="3000"
                    />
                  </div>
                  <div>
                    <Label>Tentativas</Label>
                    <Input
                      type="number"
                      min={1}
                      max={10}
                      value={form.tentativas ?? ''}
                      onChange={(e) => upd('tentativas', e.target.value ? Number(e.target.value) : undefined)}
                      placeholder="3"
                    />
                  </div>
                  <div>
                    <Label>Intervalo entre tentativas (ms)</Label>
                    <Input
                      type="number"
                      value={form.intervaloTentativas ?? ''}
                      onChange={(e) => upd('intervaloTentativas', e.target.value ? Number(e.target.value) : undefined)}
                      placeholder="2000"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    id="ajuste-tempos"
                    checked={form.ibAjusteTempos}
                    onCheckedChange={(v) => upd('ibAjusteTempos', v)}
                  />
                  <Label htmlFor="ajuste-tempos">Ajustar tempos automaticamente conforme latência</Label>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="prefeitura">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Building className="h-4 w-4" /> Identificação da prefeitura
              </CardTitle>
              <CardDescription>
                CNPJ e nome impressos no DANFSE. O logo aparece no cabeçalho do PDF gerado.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>CNPJ da prefeitura</Label>
                  <Input
                    value={form.cnpjPrefeitura ?? ''}
                    maxLength={20}
                    onChange={(e) => upd('cnpjPrefeitura', e.target.value || undefined)}
                    placeholder="00.000.000/0001-00"
                  />
                </div>
                <div>
                  <Label>Nome da prefeitura</Label>
                  <Input
                    value={form.nomePrefeitura ?? ''}
                    maxLength={120}
                    onChange={(e) => upd('nomePrefeitura', e.target.value || undefined)}
                    placeholder="Prefeitura Municipal de…"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <ImageIcon className="h-3 w-3" /> Logo da prefeitura (PNG/JPG)
                </Label>
                {config?.logoPrefeituraConfigurado && form.logoPrefeituraBase64 === undefined && (
                  <p className="text-xs text-muted-foreground">
                    Logo já configurado. Selecione um novo arquivo para substituir.
                  </p>
                )}
                <div className="flex items-center gap-3">
                  <Input
                    type="file"
                    accept="image/png,image/jpeg"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = () => upd('logoPrefeituraBase64', String(reader.result));
                      reader.readAsDataURL(file);
                    }}
                  />
                  {(form.logoPrefeituraBase64 || config?.logoPrefeituraConfigurado) && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => upd('logoPrefeituraBase64', '')}
                      className="gap-1 shrink-0"
                    >
                      <X className="h-3 w-3" /> Remover
                    </Button>
                  )}
                </div>
                {form.logoPrefeituraBase64 && form.logoPrefeituraBase64.length > 0 && (
                  <div className="border rounded p-2 bg-muted/20 inline-block">
                    <img src={form.logoPrefeituraBase64} alt="Preview" className="max-h-24" />
                  </div>
                )}
              </div>

              <div className="border-t pt-4">
                <Label>Diretório do arquivo de cidades</Label>
                <Input
                  value={form.diretorioArquivoCidades ?? ''}
                  onChange={(e) => upd('diretorioArquivoCidades', e.target.value || undefined)}
                  placeholder="C:\NFSe\Cidades"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Pasta onde o ACBr lê os arquivos de configuração das cidades suportadas.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="diretorios">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Diretórios de backup</CardTitle>
              <CardDescription>
                Pastas locais onde o servidor salvará os XMLs e PDFs autorizados.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Diretório de XMLs</Label>
                <Input
                  value={form.diretorioXml ?? ''}
                  onChange={(e) => upd('diretorioXml', e.target.value || undefined)}
                  placeholder="C:\NFSe\XML"
                />
              </div>
              <div>
                <Label>Diretório de PDFs</Label>
                <Input
                  value={form.diretorioPdf ?? ''}
                  onChange={(e) => upd('diretorioPdf', e.target.value || undefined)}
                  placeholder="C:\NFSe\PDF"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {showSaveButton && (
        <div className="flex justify-end">
          <Button onClick={submit} disabled={isSaving} className="gap-2">
            <Save className="h-4 w-4" />
            {isSaving ? 'Salvando…' : 'Salvar configuração NFS-e'}
          </Button>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
//  Página standalone (mantida para compatibilidade da rota /configuracoes/nfse)
// ──────────────────────────────────────────────────────────────────────────────

const ConfiguracaoNfse = () => {
  const { user } = useAuth();
  const empresaId = user?.empresa.id ?? 0;

  return (
    <div className="p-6 space-y-6 max-w-5xl">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <ShieldCheck className="h-7 w-7 text-primary" /> Configuração NFS-e
        </h1>
        <p className="text-muted-foreground mt-1">
          Integração direta com o Ambiente de Dados Nacional (ADN/SEFIN) — padrão NFS-e Nacional.
        </p>
      </div>

      <NfseConfigForm empresaId={empresaId} />
    </div>
  );
};

const initialForm = (): SalvarConfigNfseRequest => ({
  emiteNfse:            false,
  usaServidorNacional:  true,
  incentivadorCultural: false,
  tipoAmbiente:         2,
  ibAjusteTempos:       false,
});

export default ConfiguracaoNfse;
